import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import {
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import { DateRange, PickersShortcutsItem } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Popover from '@mui/material/Popover';
import { SaveAltRounded } from '@mui/icons-material';
import { exportToXLSX } from '../SalesPartnerPages/utils/exportToXLSX';
import { PeriodSelector } from '../../components/PeriodSelector/PeriodSelector';
import Tooltip from '@mui/material/Tooltip';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
  {
    label: 'This Week',
    getValue: () => {
      const today = dayjs();
      const dayOfWeek = today.day();
      const weekStart = today.subtract(dayOfWeek - 1, 'day');
      const weekEnd = weekStart.add(6, 'day');
      return [weekStart, weekEnd];
    },
  },
  {
    label: 'Previous Week',
    getValue: () => {
      const today = dayjs();
      const dayOfWeek = today.day();
      const prevWeekStart = today.subtract(dayOfWeek + 6, 'day');
      const prevWeekEnd = prevWeekStart.add(6, 'day');
      return [prevWeekStart, prevWeekEnd];
    },
  },
  {
    label: 'Last 7 Days',
    getValue: () => {
      const today = dayjs();
      return [today.subtract(7, 'day'), today];
    },
  },
  {
    label: 'Current Month',
    getValue: () => {
      const today = dayjs();
      return [today.startOf('month'), today.endOf('month')];
    },
  },
  {
    label: 'Next Month',
    getValue: () => {
      const today = dayjs();
      const startOfNextMonth = today.endOf('month').add(1, 'day');
      return [startOfNextMonth, startOfNextMonth.endOf('month')];
    },
  },
  { label: 'Reset', getValue: () => [null, null] },
];

export const Header = ({
  title,
  subTitle,
  hasPagination,
  rowCount,
  paginationModel,
  onPageChange,
  onPageSizeChange,
  hasDateFilter,
  refreshTableData,
  apiRef,
  showMenu,
  tableData,
  onChangeDateFilter,
  showPeriodSelector,
}: {
  title: string | undefined;
  subTitle?: string | undefined;
  hasPagination?: boolean;
  rowCount?: number;
  paginationModel?: any;
  onPageChange?: (value: any) => void;
  onPageSizeChange?: (value: any) => void;
  refreshTableData?: () => void;
  hasDateFilter?: boolean;
  apiRef?: any;
  tableData?: any;
  showMenu?: boolean;
  showPeriodSelector?: boolean;
  onChangeDateFilter?: (from: any, to: any) => void;
}) => {
  const [value, setValue] = useState<any>(['', '']);
  const [openPopover, setOpenPopover] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<any>({
    open: false,
    anchorEl: null,
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl({
      anchorEl: event.currentTarget,
      open: true,
    });
  };
  const handleClose = () => {
    setAnchorEl({
      anchorEl: null,
      open: false,
    });
  };

  useEffect(() => {
    if (value[0] && value[1]) {
      if (onChangeDateFilter) {
        onChangeDateFilter(
          value[0].format('YYYY-MM-DD'),
          value[1].format('YYYY-MM-DD')
        );
      }
    } else if (!value[0] && !value[1]) {
      if (onChangeDateFilter) {
        onChangeDateFilter('', '');
      }
    }
  }, [value]);

  return (
    <Box
      sx={{
        background: '#fff',
        borderBottomLeftRadius: '10px',
        borderBottomRightRadius: '10px',
        boxShadow: '0px 0px 10px -5px #00000091',
        p: '10px',
        mx: '10px',
        '& .MuiToolbar-root.MuiTablePagination-toolbar': {
          minHeight: 'auto',
        },
      }}
    >
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Stack direction={'row'}
               alignItems={'center'}>
          <Stack>
            <Typography variant={'h5'}>{title}</Typography>
            {subTitle && (
              <Typography variant={'caption'} color={'#666'}>
                {subTitle}
              </Typography>
            )}
          </Stack>

          {showMenu && (
            <>
              <IconButton
                size={'small'}
                aria-label="fingerprint"
                onClick={handleClick}
              >
                <MoreVertIcon fontSize={'small'} />
              </IconButton>
            </>
          )}

          {refreshTableData && (
            <Tooltip title={'Refresh table data'}>
              <IconButton
                size={'small'}
                onClick={() => {
                  refreshTableData();
                }}
              >
                <RefreshRoundedIcon fontSize={'small'} color={'primary'} />
              </IconButton>
            </Tooltip>
          )}

        </Stack>

        {showPeriodSelector && (
          <Box>
            <PeriodSelector key="periodSelector" changed={() => {}} />
          </Box>
        )}
        {hasDateFilter && (
          <>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={'en-gb'}
            >
              <DateRangePicker
                value={[value[0], value[1]]}
                slots={{ field: SingleInputDateRangeField }}
                slotProps={{
                  toolbar: {
                    hidden: true,
                  },
                  actionBar: {
                    actions: [],
                    hidden: true,
                  },
                  shortcuts: {
                    items: shortcutsItems,
                  },
                }}
                name="allowedRange"
                sx={{
                  '.MuiInputBase-root': {
                    fontSize: '0.875rem',
                    minWidth: '200px',
                    padding: '0px',
                  },
                  '.MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                      borderRadius: '50px',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.2)',
                    },
                    '& input': {
                      textAlign: 'center',
                      padding: '5px',
                    },
                  },
                }}
                onChange={(newValue, context) => {

                  if (newValue && newValue[0].isBefore(value[0]) && !context?.shortcut) {
                    newValue[1] = null;
                  }
                  setValue([newValue[0], newValue[1]]);

                  if (newValue[0] && newValue[1]) {
                    const start = new Date(
                      dayjs(newValue[0]).format('YYYY-MM-DD')
                    );
                    const end = new Date(
                      dayjs(newValue[1]).format('YYYY-MM-DD')
                    );
                    setOpenPopover(!openPopover);
                  }
                }}
                format="YYYY-MM-DD"
              />
            </LocalizationProvider>
          </>
        )}

        {hasPagination && rowCount && onPageChange && (
          <>
            <TablePagination
              sx={{
                '& .MuiTablePagination-selectLabel': {
                  display: 'none',
                },
                '& .MuiTablePagination-toolbar': {
                  paddingLeft: '0px',
                  marginLeft: '0px',
                },
                '& .MuiInputBase-root': {
                  marginRight: '8px',
                },
                border: 'none',
                marginLeft: '0px !important',
              }}
              count={rowCount}
              page={paginationModel.page}
              onPageChange={(event, page) => {
                onPageChange(page);
              }}
              rowsPerPage={paginationModel.pageSize}
              onRowsPerPageChange={(event) => {
                if (onPageSizeChange) {
                  onPageSizeChange(event.target.value);
                }
              }}
              rowsPerPageOptions={[
                10,
                25,
                50,
                { label: 'All', value: rowCount },
              ]}
              showFirstButton
              showLastButton
              size={'small'}
            />
          </>
        )}
      </Stack>

      {anchorEl?.open && (
        <Popover
          open={anchorEl.open}
          anchorEl={anchorEl.anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <List
            component="nav"
            sx={{
              '& .MuiListItemIcon-root': {
                minWidth: '36px',
              },
            }}
            onClick={() => {}}
          >
            <ListItemButton
              onClick={() => {
                if (exportToXLSX) {
                  exportToXLSX(
                    tableData?.rows,
                    tableData?.columns,
                    apiRef,
                    'TRAVEL-CITY Foglalások',
                    1
                  );
                }
              }}
            >
              <ListItemIcon>
                <SaveAltRounded color={'primary'} />
              </ListItemIcon>
              <ListItemText>Export</ListItemText>
            </ListItemButton>
          </List>
        </Popover>
      )}
    </Box>
  );
};
